/**
 * Guided Experience Provider
 *
 * Central context for step-by-step coaching system.
 * Provides:
 * - Workflow management (fetch, start, navigate)
 * - Step-by-step guidance with spotlights and tooltips
 * - Progress tracking
 * - Completeness scoring and gamification
 * - Contextual trigger detection
 *
 * AGNOSTIC DESIGN:
 * - All workflow data comes from API
 * - Step types are generic (spotlight, checklist, info, celebration)
 * - Target selectors use data attributes for flexibility
 * - No hardcoded view or entity logic
 *
 * IMPORTANT: Function definition order matters!
 * useCallback hooks that reference other useCallbacks must be defined
 * AFTER their dependencies to avoid temporal dead zone (TDZ) errors.
 */

import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { guidedExperienceAPI } from '../../../api';
import { useAuth } from '../../context/AuthContext.jsx';

const GuidedExperienceContext = createContext(null);

// Default spotlight config
const DEFAULT_SPOTLIGHT = {
  shape: 'rounded',
  padding: 8,
  color: '#f59e0b',
  transitionMs: 300,
};

export function useGuidedExperience() {
  const context = useContext(GuidedExperienceContext);
  if (!context) {
    throw new Error('useGuidedExperience must be used within a GuidedExperienceProvider');
  }
  return context;
}

export function GuidedExperienceProvider({ children }) {
  // Auth state - only fetch when authenticated
  const { isAuthenticated } = useAuth();

  // Workflow state
  const [workflows, setWorkflows] = useState([]);
  const [activeWorkflow, setActiveWorkflow] = useState(null);
  const [activeStep, setActiveStep] = useState(null);
  const [userProgress, setUserProgress] = useState({});

  // UI state
  const [isIntroOpen, setIsIntroOpen] = useState(false);
  const [isCompletionOpen, setIsCompletionOpen] = useState(false);
  const [spotlightElement, setSpotlightElement] = useState(null);
  const [spotlightRect, setSpotlightRect] = useState(null);
  const [tooltipMinimized, setTooltipMinimized] = useState(false); // User acknowledged but action pending

  // Error state for user feedback
  const [error, setError] = useState(null);
  const errorTimeoutRef = useRef(null);

  // Contextual triggers
  const [pendingTriggers, setPendingTriggers] = useState([]);
  const [currentView, setCurrentView] = useState('dashboard');

  // Refs
  const observerRef = useRef(null);
  const timeoutRef = useRef(null);
  const scrollListenerRef = useRef(null);
  const resizeListenerRef = useRef(null);
  const retryCountRef = useRef(0);
  const actionListenerRef = useRef(null); // Store action listener for cleanup
  const actionElementRef = useRef(null); // Store element with action listener
  const completeStepRef = useRef(null); // Store completeStep function for action listener
  const MAX_RETRIES = 15; // Max 3 seconds (15 * 200ms)

  // ============================================================================
  // ERROR HANDLING (no dependencies - define first)
  // ============================================================================

  /**
   * Show an error message to the user (auto-clears after 5 seconds)
   */
  const showError = useCallback(message => {
    // Clear any existing timeout
    if (errorTimeoutRef.current) {
      clearTimeout(errorTimeoutRef.current);
    }
    setError(message);
    // Auto-clear after 5 seconds
    errorTimeoutRef.current = setTimeout(() => {
      setError(null);
    }, 5000);
  }, []);

  /**
   * Clear the current error message
   */
  const clearError = useCallback(() => {
    if (errorTimeoutRef.current) {
      clearTimeout(errorTimeoutRef.current);
    }
    setError(null);
  }, []);

  // ============================================================================
  // FETCH WORKFLOWS (no dependencies on other callbacks)
  // ============================================================================
  const fetchWorkflows = useCallback(async () => {
    try {
      const res = await guidedExperienceAPI.getWorkflows();
      if (res.ok && res.data) {
        setWorkflows(res.data);
        // Build progress map
        const progressMap = {};
        res.data.forEach(w => {
          if (w.user_progress) {
            progressMap[w.workflow_code] = w.user_progress;
          }
        });
        setUserProgress(progressMap);
      }
    } catch (err) {
      console.error('Failed to fetch workflows:', err);
    }
  }, []);

  // Fetch only when authenticated
  useEffect(() => {
    if (isAuthenticated) {
      fetchWorkflows();
    } else {
      // Reset state when logged out
      setWorkflows([]);
      setUserProgress({});
    }
  }, [isAuthenticated, fetchWorkflows]);

  // ============================================================================
  // CONTEXTUAL TRIGGERS (no dependencies on spotlight functions)
  // ============================================================================
  const checkTriggers = useCallback(async view => {
    try {
      const res = await guidedExperienceAPI.getTriggers({ view });
      if (res.ok && res.data && res.data.length > 0) {
        setPendingTriggers(res.data);
      } else {
        setPendingTriggers([]);
      }
    } catch (err) {
      console.error('Failed to check triggers:', err);
    }
  }, []);

  // Check triggers when view changes (only if authenticated)
  useEffect(() => {
    if (isAuthenticated && currentView) {
      checkTriggers(currentView);
    }
  }, [isAuthenticated, currentView, checkTriggers]);

  // ============================================================================
  // VIEW TRACKING (no dependencies - simple setter)
  // ============================================================================
  const updateView = useCallback(view => {
    setCurrentView(view);
  }, []);

  // ============================================================================
  // TOOLTIP STATE (no dependencies - simple setters)
  // ============================================================================

  /**
   * Minimize tooltip (user acknowledges but action still pending)
   */
  const minimizeTooltip = useCallback(() => {
    setTooltipMinimized(true);
  }, []);

  /**
   * Restore tooltip
   */
  const restoreTooltip = useCallback(() => {
    setTooltipMinimized(false);
  }, []);

  /**
   * Close completion modal and cleanup
   */
  const closeCompletion = useCallback(() => {
    setIsCompletionOpen(false);
    setActiveWorkflow(null);
    setActiveStep(null);
  }, []);

  // ============================================================================
  // COMPLETENESS (no dependencies on spotlight functions)
  // ============================================================================
  const getCompleteness = useCallback(async (entityType, entityId) => {
    try {
      const res = await guidedExperienceAPI.getCompleteness(entityType, entityId);
      return res.ok ? res.data : null;
    } catch (err) {
      console.error('Failed to get completeness:', err);
      return null;
    }
  }, []);

  const calculateCompleteness = useCallback(async (entityType, entityId) => {
    try {
      const res = await guidedExperienceAPI.calculateCompleteness(entityType, entityId);
      return res.ok ? res.data : null;
    } catch (err) {
      console.error('Failed to calculate completeness:', err);
      return null;
    }
  }, []);

  // ============================================================================
  // SPOTLIGHT MANAGEMENT - LEVEL 1 (no callback dependencies)
  // ============================================================================

  /**
   * Clear all event listeners for spotlight
   */
  const clearEventListeners = useCallback(() => {
    if (scrollListenerRef.current) {
      // Must use same options as addEventListener (capture: true)
      window.removeEventListener('scroll', scrollListenerRef.current, { capture: true });
      scrollListenerRef.current = null;
    }
    if (resizeListenerRef.current) {
      window.removeEventListener('resize', resizeListenerRef.current);
      resizeListenerRef.current = null;
    }
  }, []);

  /**
   * Clear action listener from element
   */
  const clearActionListener = useCallback(() => {
    if (actionListenerRef.current && actionElementRef.current) {
      actionElementRef.current.removeEventListener('click', actionListenerRef.current, true);
      actionListenerRef.current = null;
      actionElementRef.current = null;
    }
  }, []);

  /**
   * Update spotlight rect from element
   */
  const updateSpotlightRect = useCallback(
    el => {
      if (!el) return;
      const rect = el.getBoundingClientRect();
      const padding = activeStep?.spotlight_padding || DEFAULT_SPOTLIGHT.padding;
      setSpotlightRect({
        top: rect.top - padding,
        left: rect.left - padding,
        width: rect.width + padding * 2,
        height: rect.height + padding * 2,
        shape: activeStep?.spotlight_shape || DEFAULT_SPOTLIGHT.shape,
        color: activeStep?.highlight_color || DEFAULT_SPOTLIGHT.color,
      });
    },
    [activeStep]
  );

  /**
   * Attach action listener to target element
   * This detects when user actually performs the action
   */
  const attachActionListener = useCallback(el => {
    // Clear any previous listener
    if (actionListenerRef.current && actionElementRef.current) {
      actionElementRef.current.removeEventListener('click', actionListenerRef.current, true);
    }

    // Create new listener with debounce protection
    let isProcessing = false;
    const handleActionClick = () => {
      if (isProcessing) return;
      isProcessing = true;

      // Debug: Action detected on target element

      // Small delay to let the original click action complete first
      setTimeout(() => {
        if (completeStepRef.current) {
          completeStepRef.current();
        }
        isProcessing = false;
      }, 100);
    };

    // Attach listener with capture to intercept early
    el.addEventListener('click', handleActionClick, true);

    // Store for cleanup
    actionListenerRef.current = handleActionClick;
    actionElementRef.current = el;

    // Debug: Action listener attached
  }, []);

  // ============================================================================
  // SPOTLIGHT MANAGEMENT - LEVEL 2 (depends on Level 1)
  // ============================================================================

  /**
   * Observe element for position/size changes
   */
  const observeElement = useCallback(
    el => {
      // Clean up previous observers and listeners
      if (observerRef.current) {
        observerRef.current.disconnect();
      }
      clearEventListeners();

      // Set up ResizeObserver
      observerRef.current = new ResizeObserver(() => {
        updateSpotlightRect(el);
      });
      observerRef.current.observe(el);

      // Set up scroll/resize listeners (stored in refs for cleanup)
      const handleUpdate = () => updateSpotlightRect(el);
      scrollListenerRef.current = handleUpdate;
      resizeListenerRef.current = handleUpdate;

      window.addEventListener('scroll', handleUpdate, { passive: true, capture: true });
      window.addEventListener('resize', handleUpdate, { passive: true });
    },
    [updateSpotlightRect, clearEventListeners]
  );

  /**
   * Clear spotlight and all associated resources
   */
  const clearSpotlight = useCallback(() => {
    if (observerRef.current) {
      observerRef.current.disconnect();
      observerRef.current = null;
    }
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
    clearEventListeners();
    clearActionListener();
    retryCountRef.current = 0;
    setSpotlightElement(null);
    setSpotlightRect(null);
    setTooltipMinimized(false);
  }, [clearEventListeners, clearActionListener]);

  // ============================================================================
  // SPOTLIGHT MANAGEMENT - LEVEL 3 (depends on Level 2)
  // ============================================================================

  /**
   * Find element by selector and spotlight it
   * Respects target_view - won't search if not on the correct view
   */
  const spotlightStep = useCallback(
    step => {
      // Reset retry counter
      retryCountRef.current = 0;

      if (!step?.target_selector) {
        setSpotlightElement(null);
        setSpotlightRect(null);
        return;
      }

      // Check if we're on the correct view for this step
      // Sidebar items (no target_view or target_view=null) are always searchable
      if (step.target_view && step.target_view !== currentView) {
        // Not on the correct view - don't search, clear spotlight
        // The sidebar item for navigation should still be highlightable
        console.info(
          `[GuidedExperience] Step requires view "${step.target_view}" but current view is "${currentView}"`
        );
        setSpotlightElement(null);
        setSpotlightRect(null);
        return;
      }

      // Find target element with retry logic
      const findElement = () => {
        const el = document.querySelector(step.target_selector);
        if (el) {
          setSpotlightElement(el);
          updateSpotlightRect(el);
          observeElement(el);

          // IMPORTANT: Always clear previous action listener before potentially attaching new one
          // This prevents orphaned listeners from triggering on old elements
          clearActionListener();

          // Only attach action listener for 'spotlight' steps that require user action
          // 'info' and 'checklist' steps should NOT auto-complete on click
          if (step.step_type === 'spotlight') {
            attachActionListener(el);
          }
          setTooltipMinimized(false); // Reset tooltip state for new step
          retryCountRef.current = 0; // Reset on success
          // Debug: Element found and spotlighted
        } else {
          // Element not found - retry with limit
          retryCountRef.current++;
          if (retryCountRef.current < MAX_RETRIES) {
            if (timeoutRef.current) clearTimeout(timeoutRef.current);
            timeoutRef.current = setTimeout(findElement, 200);
          } else {
            // Max retries reached - show step without spotlight
            console.warn(
              `[GuidedExperience] Element not found after ${MAX_RETRIES} retries:`,
              step.target_selector
            );
            setSpotlightElement(null);
            setSpotlightRect(null);
          }
        }
      };

      findElement();
    },
    [updateSpotlightRect, observeElement, currentView, attachActionListener, clearActionListener]
  );

  // ============================================================================
  // WORKFLOW CONTROL - LEVEL 1 (depends on spotlight functions)
  // ============================================================================

  /**
   * Start a workflow - shows intro modal
   */
  const startWorkflow = useCallback(
    async workflowCode => {
      if (!isAuthenticated) {
        console.warn('Cannot start workflow: user not authenticated');
        return;
      }

      try {
        // Fetch full workflow with steps
        const res = await guidedExperienceAPI.getWorkflow(workflowCode);
        if (res.ok && res.data) {
          setActiveWorkflow(res.data);
          setIsIntroOpen(true);

          // Notify API that we started
          await guidedExperienceAPI.startWorkflow(workflowCode);
        } else {
          showError('Impossible de charger le guide. Veuillez reessayer.');
        }
      } catch (err) {
        console.error('Failed to start workflow:', err);
        showError('Erreur lors du chargement du guide.');
      }
    },
    [isAuthenticated, showError]
  );

  /**
   * Begin workflow after intro modal
   */
  const beginWorkflow = useCallback(() => {
    setIsIntroOpen(false);
    if (activeWorkflow?.steps?.length > 0) {
      const firstStep = activeWorkflow.steps[0];
      setActiveStep(firstStep);
      spotlightStep(firstStep);
    }
  }, [activeWorkflow, spotlightStep]);

  /**
   * Skip/dismiss the current workflow
   */
  const skipWorkflow = useCallback(async () => {
    if (activeWorkflow) {
      await guidedExperienceAPI.skipWorkflow(activeWorkflow.workflow_code);
    }
    setActiveWorkflow(null);
    setActiveStep(null);
    setIsIntroOpen(false);
    clearSpotlight();
  }, [activeWorkflow, clearSpotlight]);

  /**
   * Complete current step and move to next
   */
  const completeStep = useCallback(async () => {
    if (!activeWorkflow || !activeStep) return;

    try {
      const res = await guidedExperienceAPI.completeStep(
        activeWorkflow.workflow_code,
        activeStep.step_order
      );

      if (res.ok) {
        if (res.isComplete) {
          // Workflow completed!
          setIsCompletionOpen(true);
          clearSpotlight();
        } else {
          // Move to next step
          const nextStepOrder = res.nextStep;
          const nextStep = activeWorkflow.steps.find(s => s.step_order === nextStepOrder);
          if (nextStep) {
            setActiveStep(nextStep);
            spotlightStep(nextStep);
          }
        }

        // Update progress
        setUserProgress(prev => ({
          ...prev,
          [activeWorkflow.workflow_code]: res.data,
        }));
      } else {
        showError('Impossible de valider cette etape. Veuillez reessayer.');
      }
    } catch (err) {
      console.error('Failed to complete step:', err);
      showError("Erreur lors de la validation de l'etape.");
    }
  }, [activeWorkflow, activeStep, clearSpotlight, spotlightStep, showError]);

  /**
   * Skip current step (if allowed)
   */
  const skipStep = useCallback(() => {
    if (!activeStep?.can_skip) return;
    completeStep();
  }, [activeStep, completeStep]);

  // Keep completeStepRef updated with latest completeStep function
  useEffect(() => {
    completeStepRef.current = completeStep;
  }, [completeStep]);

  // ============================================================================
  // WORKFLOW RESET (depends on fetchWorkflows)
  // ============================================================================

  /**
   * Reset a workflow to allow replaying it
   */
  const resetWorkflow = useCallback(
    async workflowCode => {
      if (!isAuthenticated) {
        console.warn('Cannot reset workflow: user not authenticated');
        return { ok: false, error: 'Not authenticated' };
      }

      try {
        const res = await guidedExperienceAPI.resetWorkflow(workflowCode);
        if (res.ok) {
          // Refresh workflows to update progress
          await fetchWorkflows();
          // Workflow reset success
        }
        return res;
      } catch (err) {
        console.error('Failed to reset workflow:', err);
        return { ok: false, error: err.message };
      }
    },
    [isAuthenticated, fetchWorkflows]
  );

  /**
   * Reset all workflows (for testing/demo)
   */
  const resetAllWorkflows = useCallback(async () => {
    if (!isAuthenticated) {
      console.warn('Cannot reset workflows: user not authenticated');
      return { ok: false, error: 'Not authenticated' };
    }

    try {
      const res = await guidedExperienceAPI.resetAllWorkflows();
      if (res.ok) {
        // Refresh workflows to update progress
        await fetchWorkflows();
        // All workflows reset success
      }
      return res;
    } catch (err) {
      console.error('Failed to reset all workflows:', err);
      return { ok: false, error: err.message };
    }
  }, [isAuthenticated, fetchWorkflows]);

  // ============================================================================
  // CLEANUP EFFECTS
  // ============================================================================

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (observerRef.current) observerRef.current.disconnect();
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
      if (errorTimeoutRef.current) clearTimeout(errorTimeoutRef.current);
      clearEventListeners();
      clearActionListener();
    };
  }, [clearEventListeners, clearActionListener]);

  // Re-spotlight when view changes (user navigated to correct view)
  useEffect(() => {
    if (activeStep && currentView) {
      // Re-attempt spotlight now that view changed
      spotlightStep(activeStep);
    }
  }, [currentView, activeStep, spotlightStep]);

  // ============================================================================
  // CONTEXT VALUE
  // ============================================================================
  // CRITICAL FIX: Memoize value to prevent infinite re-renders
  // Without useMemo, a new object is created on every render, causing all consumers to re-render
  const value = React.useMemo(() => ({
    // Data
    workflows,
    activeWorkflow,
    activeStep,
    userProgress,
    pendingTriggers,
    currentView,

    // Spotlight
    spotlightElement,
    spotlightRect,

    // Tooltip state
    tooltipMinimized,

    // Modals
    isIntroOpen,
    isCompletionOpen,

    // Error feedback
    error,
    clearError,

    // Actions
    fetchWorkflows,
    startWorkflow,
    beginWorkflow,
    skipWorkflow,
    completeStep,
    skipStep,
    closeCompletion,
    clearSpotlight,
    updateView,
    minimizeTooltip,
    restoreTooltip,

    // Completeness
    getCompleteness,
    calculateCompleteness,

    // Triggers
    checkTriggers,

    // Reset
    resetWorkflow,
    resetAllWorkflows,
  }), [
    workflows,
    activeWorkflow,
    activeStep,
    userProgress,
    pendingTriggers,
    currentView,
    spotlightElement,
    spotlightRect,
    tooltipMinimized,
    isIntroOpen,
    isCompletionOpen,
    error,
    clearError,
    fetchWorkflows,
    startWorkflow,
    beginWorkflow,
    skipWorkflow,
    completeStep,
    skipStep,
    closeCompletion,
    clearSpotlight,
    updateView,
    minimizeTooltip,
    restoreTooltip,
    getCompleteness,
    calculateCompleteness,
    checkTriggers,
    resetWorkflow,
    resetAllWorkflows,
  ]);

  return (
    <GuidedExperienceContext.Provider value={value}>{children}</GuidedExperienceContext.Provider>
  );
}

export default GuidedExperienceProvider;
